import { datadogRum } from '@datadog/browser-rum'

// `performance.memory` is non-standard and Chrome-only (deprecated but still
// shipping). It reports whole-context heap totals with no per-realm breakdown.
// We sample it on a relaxed interval and ship each reading to Datadog as a RUM
// action so heap growth correlates with the session, view, and CPU profile on
// one timeline. Accurate per-realm memory would need
// `measureUserAgentSpecificMemory()`, which requires cross-origin isolation
// (COOP + COEP) that conflicts with the portal's cross-origin iframes.
// https://developer.mozilla.org/en-US/docs/Web/API/Performance/measureUserAgentSpecificMemory
// https://developer.mozilla.org/en-US/docs/Web/API/Performance/memory
interface LegacyMemory {
  jsHeapSizeLimit: number
  totalJSHeapSize: number
  usedJSHeapSize: number
}

const SAMPLE_INTERVAL_MS = 10_000

const readMemory = (): LegacyMemory | undefined =>
  (performance as Performance & { memory?: LegacyMemory }).memory

const sampleMemory = (): void => {
  const mem = readMemory()
  if (!mem) {
    return
  }
  datadogRum.addAction('memory.sample', {
    usedJSHeapSize: mem.usedJSHeapSize,
    totalJSHeapSize: mem.totalJSHeapSize,
    jsHeapSizeLimit: mem.jsHeapSizeLimit,
    usagePct: mem.usedJSHeapSize / mem.jsHeapSizeLimit,
  })
}

let startedMemorySampling = false
// Starts periodic heap sampling. No-op when `performance.memory` is absent
// (non-Chrome), so safe to call unconditionally after RUM init.
export const startMemorySampling = (): void => {
  if (startedMemorySampling) {
    return
  }
  startedMemorySampling = true
  if (!readMemory()) {
    console.warn('unable to sample memory')
    return
  }
  sampleMemory()
  setInterval(sampleMemory, SAMPLE_INTERVAL_MS)
}
